/**
 * features/inbox/hooks — hooks de leitura do inbox (TASK-14).
 *
 * useTenantQuery garante que a queryKey seja prefixada por tenantId +
 * businessUnitId (DD-004, RNF 2.3), evitando que dados de uma BU sejam
 * exibidos após a troca de BU ativa.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { InboxItemData } from "./types.js";
import { listInbox, markInboxItemRead } from "../../data/repos/inboxRepo.js";
import { useTenantQuery, useTenantScope } from "../../app/providers/QueryProvider.js";

export interface UseInboxResult {
  items: InboxItemData[];
  loading: boolean;
  error: string | null;
  /** Código de erro de markAsRead quando o PATCH /read falha; null em sucesso ou idle. */
  markReadError: string | null;
  markAsRead: (id: string) => void;
  refetch: () => void;
}

export function useInbox(): UseInboxResult {
  const queryClient = useQueryClient();
  const scope = useTenantScope();
  const [markReadError, setMarkReadError] = useState<string | null>(null);

  // Área "inbox" — a MESMA que a InboxPage usa ao chamar useTenantQuery diretamente. Precisam
  // coincidir: a página invalida por chave EXATA (buildQueryKey("inbox", ...)) ao marcar um item
  // como lido, e uma área distinta aqui deixaria o badge da navegação exibindo a contagem antiga
  // depois de o usuário ler tudo — de volta ao defeito que este trabalho corrige, só que por
  // outro caminho. Chave única também significa uma requisição, não duas, para o mesmo dado.
  const query = useTenantQuery(
    "inbox",
    () => listInbox(),
    { staleTime: 15_000 },
  );

  const markMutation = useMutation<void, Error, string>({
    mutationFn: (id) => markInboxItemRead(id),
    onSuccess: () => {
      setMarkReadError(null);
      // Invalida a query de inbox do escopo corrente.
      void queryClient.invalidateQueries({
        predicate: (q) => {
          const key = q.queryKey as unknown[];
          return key[0] === scope.tenantId && String(key[2]).startsWith("inbox");
        },
      });
    },
    onError: () => {
      // Registra o erro sem lançar uma unhandled rejection; a UI pode exibir
      // via markReadError. O item permanece marcado como não-lido (sem rollback
      // otimista para evitar inconsistência visual).
      setMarkReadError("WEB-ERR-013");
    },
  });

  function markAsRead(id: string) {
    setMarkReadError(null);
    markMutation.mutate(id);
  }

  return {
    items: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    markReadError,
    markAsRead,
    refetch: () => { void query.refetch(); },
  };
}
